import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GoogleLogo, ArrowRight, LockKey, ShieldCheck, LinkSimple, Copy } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

const BG = "https://images.unsplash.com/photo-1638184984605-af1f05249a56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNoJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODc2NDk0NTJ8MA&ixlib=rb-4.1.0&q=85";

export default function AuthPage() {
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.email, form.password, form.name);
      }
      toast.success("Welcome to SafeShort");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505]">
      {/* form side */}
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-20 py-12 grid-bg">
        <div className="max-w-md w-full mx-auto fade-up">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-8 h-8 border border-white/30 flex items-center justify-center">
              <ShieldCheck size={18} weight="bold" className="text-white" />
            </div>
            <span className="font-head font-extrabold tracking-tighter text-lg">SAFESHORT</span>
          </div>

          <p className="label-xs mb-3">{mode === "login" ? "// access terminal" : "// create account"}</p>
          <h1 className="font-head text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-[0.95] mb-3">
            {mode === "login" ? "Sign in" : "Register"}
          </h1>
          <p className="text-zinc-400 text-sm mb-8 leading-relaxed">
            Instant open-redirect link shortener. Shorten anything — no account required.
          </p>

          <QuickShorten />

          <div className="flex items-center gap-4 my-8">
            <div className="h-px flex-1 bg-white/10" />
            <span className="label-xs">{mode === "login" ? "sign in for a dashboard" : "or register"}</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field label="Name" testid="auth-name-input" value={form.name} onChange={set("name")} placeholder="Ada Lovelace" required />
            )}
            <Field label="Email" testid="auth-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@domain.com" required />
            <Field label="Password" testid="auth-password-input" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" required />

            {mode === "login" && (
              <button type="button" onClick={() => navigate("/reset-password")}
                data-testid="forgot-password-link"
                className="label-xs hover:text-white transition-colors">forgot password?</button>
            )}

            <button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full h-12 bg-white text-black font-head font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-zinc-300 transition-colors disabled:opacity-50">
              {loading ? "processing..." : (mode === "login" ? "Enter" : "Create account")}
              {!loading && <ArrowRight size={16} weight="bold" />}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="label-xs">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <button onClick={loginWithGoogle} data-testid="google-login-btn"
            className="w-full h-12 border border-white/20 text-white font-head text-sm uppercase tracking-wider flex items-center justify-center gap-3 hover:border-white hover:bg-white/5 transition-colors">
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </button>

          <p className="text-sm text-zinc-500 mt-8">
            {mode === "login" ? "No account yet?" : "Already registered?"}{" "}
            <button data-testid="auth-mode-toggle"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-white underline underline-offset-4 hover:text-zinc-300 transition-colors">
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </div>

      {/* image side */}
      <div className="hidden lg:block relative border-l border-white/10">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <div className="flex items-center gap-2 label-xs mb-4"><LockKey size={14} /> open redirect mode</div>
          <div className="font-head text-2xl font-bold tracking-tight leading-snug max-w-sm">
            Any URL. Instant 301. No account, no interstitial, no gatekeeping.
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickShorten() {
  const [url, setUrl] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const shorten = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/links", { destination_url: url });
      let full;
      try {
        const h = data.short_url ? new URL(data.short_url).hostname : "";
        full = (h.includes(".") && !h.includes("localhost")) ? data.short_url : `${window.location.origin}${data.short_path}`;
      } catch {
        full = `${window.location.origin}${data.short_path}`;
      }
      setResult(full);
      setUrl("");
      try { await navigator.clipboard.writeText(full); toast.success("Short link created & copied"); }
      catch { toast.success("Short link created"); }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const copy = () => { navigator.clipboard.writeText(result); toast.success("Copied"); };

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-4" data-testid="public-shortener">
      <p className="label-xs mb-3 flex items-center gap-2"><LinkSimple size={13} /> quick shorten</p>
      <form onSubmit={shorten} className="flex gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} required placeholder="paste any url"
          data-testid="public-url-input"
          className="flex-1 h-11 bg-transparent border border-white/10 px-3 text-sm mono placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors" />
        <button type="submit" disabled={loading} data-testid="public-shorten-btn"
          className="h-11 px-5 bg-white text-black font-head font-bold text-sm uppercase tracking-wider hover:bg-zinc-300 transition-colors disabled:opacity-50">
          {loading ? "..." : "Go"}
        </button>
      </form>
      {result && (
        <div className="mt-3 flex items-center justify-between gap-2 border border-white/10 px-3 py-2" data-testid="public-result">
          <a href={result} target="_blank" rel="noreferrer" className="mono text-xs text-white truncate hover:underline">{result}</a>
          <button onClick={copy} data-testid="public-copy-btn" className="text-zinc-500 hover:text-white transition-colors shrink-0"><Copy size={15} /></button>
        </div>
      )}
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="label-xs block mb-2">{label}</span>
      <input {...props} data-testid={testid}
        className="w-full h-12 bg-transparent border border-white/10 px-4 text-white text-sm mono placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors" />
    </label>
  );
}
